// Google Tag Manager Data Layer Utilities

declare global {
  interface Window {
    dataLayer: any[];
  }
}

// Initialize dataLayer if it doesn't exist
if (typeof window !== 'undefined' && !window.dataLayer) {
  window.dataLayer = [];
}

// Helper function to push to dataLayer
const pushToDataLayer = (data: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(data);
    try {
      console.log('GTM Event:', data);
    } catch (error) {
      // Silently catch console.log errors caused by browser extensions
    }
  }
};

// ============================================
// 1. USER AUTHENTICATION & PROFILE TRACKING
// ============================================

export const trackUserLogin = (userId: string, userStatus: 'free' | 'subscribed', subscriptionTier: 'none' | 'basic' | 'premium', registrationDate?: string) => {
  pushToDataLayer({
    event: 'user_login',
    user_id: userId,
    user_status: userStatus,
    subscription_tier: subscriptionTier,
    registration_date: registrationDate || new Date().toISOString().split('T')[0]
  });
};

export const trackUserRegistration = (userId: string) => {
  pushToDataLayer({
    event: 'user_registration',
    user_id: userId,
    registration_date: new Date().toISOString().split('T')[0]
  });
};

// ============================================
// 2. READING FUNNEL TRACKING
// ============================================

export const trackReadingStarted = (readingType: '3_cards' | '9_cards', questionCategory: 'love' | 'career' | 'health' | 'general' | 'custom', userType: 'anonymous' | 'registered' | 'subscribed') => {
  const readingId = `reading_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  pushToDataLayer({
    event: 'reading_started',
    reading_type: readingType,
    question_category: questionCategory,
    user_type: userType,
    reading_id: readingId
  });
  
  // Store reading ID in sessionStorage for tracking throughout the funnel
  if (typeof window !== 'undefined') {
    sessionStorage.setItem('current_reading_id', readingId);
  }
  
  return readingId;
};

export const trackCardSelected = (cardPosition: number, totalCards: 3 | 9, cardName?: string) => {
  const readingId = typeof window !== 'undefined' ? sessionStorage.getItem('current_reading_id') : null;
  
  pushToDataLayer({
    event: 'card_selected',
    card_position: cardPosition,
    total_cards: totalCards,
    card_name: cardName,
    reading_id: readingId
  });
};

export const trackReadingCompleted = (readingType: '3_cards' | '9_cards', isFreeReading: boolean) => {
  const readingId = typeof window !== 'undefined' ? sessionStorage.getItem('current_reading_id') : null;
  
  pushToDataLayer({
    event: 'reading_completed',
    reading_type: readingType,
    reading_id: readingId,
    is_free_reading: isFreeReading
  });
  
  // Clear reading ID after completion
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem('current_reading_id');
  }
};

// ============================================
// 3. E-COMMERCE & SUBSCRIPTION EVENTS
// ============================================

export const trackViewSubscriptionPlans = (plansDisplayed: string[]) => {
  pushToDataLayer({
    event: 'view_subscription_plans',
    plans_displayed: plansDisplayed
  });
};

export const trackBeginCheckout = (subscriptionTier: 'basic' | 'premium', subscriptionPrice: number, currency: string = 'CZK') => {
  pushToDataLayer({
    event: 'begin_checkout',
    subscription_tier: subscriptionTier,
    subscription_price: subscriptionPrice,
    currency: currency
  });
};

export const trackPurchase = (transactionId: string, value: number, subscriptionTier: 'basic' | 'premium', currency: string = 'CZK') => {
  pushToDataLayer({
    event: 'purchase',
    transaction_id: transactionId,
    value: value,
    currency: currency,
    subscription_tier: subscriptionTier
  });
};

export const trackAddPaymentInfo = (paymentMethod: string) => {
  pushToDataLayer({
    event: 'add_payment_info',
    payment_method: paymentMethod
  });
};

// ============================================
// 4. ENGAGEMENT & CONTENT INTERACTION
// ============================================

export const trackCardViewed = (cardName: string, cardNumber: string) => {
  pushToDataLayer({
    event: 'card_viewed',
    card_name: cardName,
    card_number: cardNumber
  });
};

export const trackViewReadingHistory = (totalReadings: number, readingsThisMonth: number) => {
  pushToDataLayer({
    event: 'view_reading_history',
    total_readings: totalReadings,
    readings_this_month: readingsThisMonth
  });
};

export const trackQuestionInputFocus = (page: string, userType: 'anonymous' | 'registered' | 'subscribed') => {
  pushToDataLayer({
    event: 'question_input_focus',
    page: page,
    user_type: userType
  });
};

export const trackShuffleCards = (page: string) => {
  pushToDataLayer({
    event: 'shuffle_cards',
    page: page
  });
};

// ============================================
// 5. LANGUAGE & LOCALIZATION
// ============================================

export const trackLanguageChanged = (previousLanguage: string, newLanguage: string, page: string) => {
  pushToDataLayer({
    event: 'language_changed',
    previous_language: previousLanguage,
    new_language: newLanguage,
    page: page
  });
};

// ============================================
// 6. EXIT INTENT & ABANDONMENT TRACKING
// ============================================

export const trackReadingAbandoned = (abandonmentStage: 'question' | 'card_selection' | 'checkout', cardsSelected?: number, totalRequired?: number) => {
  const readingId = typeof window !== 'undefined' ? sessionStorage.getItem('current_reading_id') : null;
  
  pushToDataLayer({
    event: 'reading_abandoned',
    abandonment_stage: abandonmentStage,
    cards_selected: cardsSelected,
    total_required: totalRequired,
    reading_id: readingId
  });
};

export const trackCheckoutAbandoned = (subscriptionTier: 'basic' | 'premium', value: number) => {
  pushToDataLayer({
    event: 'checkout_abandoned',
    subscription_tier: subscriptionTier,
    value: value
  });
};

export const trackExitIntent = (page: string, timeOnPage: number) => {
  pushToDataLayer({
    event: 'exit_intent',
    page: page,
    time_on_page: timeOnPage
  });
};

// ============================================
// 7. ERROR TRACKING
// ============================================

export const trackError = (errorType: 'payment_failed' | 'translation_missing' | 'api_error' | 'card_load_error' | 'general_error', errorMessage: string, page: string) => {
  pushToDataLayer({
    event: 'error_occurred',
    error_type: errorType,
    error_message: errorMessage,
    page: page
  });
};

// ============================================
// 8. PAGE VIEW TRACKING
// ============================================

export const trackPageView = (pagePath: string, pageTitle: string) => {
  pushToDataLayer({
    event: 'page_view',
    page_path: pagePath,
    page_title: pageTitle
  });
};

// ============================================
// 9. UTILITY FUNCTIONS
// ============================================

// Get user type from localStorage/auth state
export const getUserType = (): 'anonymous' | 'registered' | 'subscribed' => {
  if (typeof window === 'undefined') return 'anonymous';
  
  const userAuth = localStorage.getItem('userAuth');
  const userSubscribed = localStorage.getItem('userSubscribed') === 'true';
  
  if (!userAuth) return 'anonymous';
  if (userSubscribed) return 'subscribed';
  return 'registered';
};

// Categorize question based on keywords
export const categorizeQuestion = (question: string): 'love' | 'career' | 'health' | 'general' | 'custom' => {
  const lowerQuestion = question.toLowerCase();
  
  const loveKeywords = ['láska', 'vztah', 'partner', 'manžel', 'manželka', 'milovat', 'love', 'relationship'];
  const careerKeywords = ['práce', 'kariéra', 'zaměstnání', 'podnikání', 'career', 'job', 'work'];
  const healthKeywords = ['zdraví', 'nemoc', 'léčba', 'health', 'illness'];
  
  if (loveKeywords.some(keyword => lowerQuestion.includes(keyword))) return 'love';
  if (careerKeywords.some(keyword => lowerQuestion.includes(keyword))) return 'career';
  if (healthKeywords.some(keyword => lowerQuestion.includes(keyword))) return 'health';
  if (question.trim() === '') return 'general';
  
  return 'custom';
};

// Track time on page for exit intent
export const initializePageTimer = () => {
  if (typeof window === 'undefined') return;
  
  const startTime = Date.now();
  sessionStorage.setItem('page_start_time', startTime.toString());
};

export const getTimeOnPage = (): number => {
  if (typeof window === 'undefined') return 0;
  
  const startTime = sessionStorage.getItem('page_start_time');
  if (!startTime) return 0;
  
  return Math.floor((Date.now() - parseInt(startTime)) / 1000); // Return seconds
};

// ============================================
// 10. GOOGLE CONSENT MODE V2
// ============================================

export interface ConsentState {
  analytics: boolean;
  advertising: boolean;
  personalization: boolean;
}

export const CONSENT_STORAGE_KEY = 'cookie_consent_v2';

export const updateGoogleConsent = (consent: ConsentState) => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(['consent', 'update', {
    ad_storage: consent.advertising ? 'granted' : 'denied',
    ad_user_data: consent.advertising ? 'granted' : 'denied',
    ad_personalization: consent.personalization ? 'granted' : 'denied',
    analytics_storage: consent.analytics ? 'granted' : 'denied',
  }]);
  pushToDataLayer({
    event: 'consent_update',
    consent_analytics: consent.analytics ? 'granted' : 'denied',
    consent_advertising: consent.advertising ? 'granted' : 'denied',
    consent_personalization: consent.personalization ? 'granted' : 'denied',
  });
};

export const saveConsent = (consent: ConsentState) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
  updateGoogleConsent(consent);
};

export const restoreConsent = () => {
  if (typeof window === 'undefined') return false;
  const saved = localStorage.getItem(CONSENT_STORAGE_KEY);
  if (!saved) return false;
  try {
    const consent: ConsentState = JSON.parse(saved);
    updateGoogleConsent(consent);
    return true;
  } catch {
    return false;
  }
};

export const hasConsentBeenSet = (): boolean => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(CONSENT_STORAGE_KEY) !== null;
};